// @ts-strict-ignore
import isEmpty from 'lodash/isEmpty';
import isNumber from 'lodash/isNumber';
import keyBy from 'lodash/keyBy';
import moment from 'moment';

import {pluralize} from 'common/util/string';
import {ALL_MATH_TYPES} from 'common/report/constants';
import {
  getBehaviorShowClauses,
  isBehaviorShowClause,
  isFunnelOnlyInsightsBookmark,
} from 'common/report/insights/models/utils';
import {getSeriesDisplayHeaders} from 'common/report/insights/util';
import {AnalysisTypeLabelKey, DisplayOptions, InsightsQueryResults} from 'common/report/insights/util/types';
import {
  AnalysisType,
  ChartPlotStyle,
  ChartType,
  FUNNELS_MATH_TYPES,
  InsightsBookmarkParams,
  InsightsResourceType,
  MATH_TYPE,
  RETENTION_MATH_TYPES,
} from 'common/types/reports/bookmark';
import {
  END_OF_TODAY,
  END_OF_YESTERDAY,
  START_OF_TODAY,
  START_OF_YESTERDAY,
  X_TO_DATE,
} from 'common/widgets/date-range-picker/models/presets';
import {DateRange} from 'common/widgets/date-range-picker/models/date-range';
import {Bookmark, DashboardCardChartOptions, TimeFilter} from 'common/report/dashboards/types';
import {DataGroup} from 'common/widgets/data-group-selector/types';
import {LocalizationStore} from 'common/i18n/store';
import {Translatable} from 'common/i18n';
import {OperatorToString} from 'common/query-builder/query-block/count-menu/constants';

import {getXToDateText} from '../util/dashboard';
import {MomentDateFormat} from '../models/enums/datetime';
import {DateRangeType, DeprecatedChartType} from '../models/enums/report';
import {INSIGHTS_CHART_TO_DISPLAY} from '../models/constants';
import {getTimeComparisonText} from './index';

function getChartType(bookmarkParams, chartOptions): ChartType {
  let chartType = chartOptions && chartOptions.type;
  if (!chartType) {
    const displayOptions = getDisplayOptions(bookmarkParams, chartOptions);
    chartType = displayOptions.chartType;
    if (displayOptions.plotStyle === ChartPlotStyle.Stacked) {
      chartType = `${chartType}-${displayOptions.plotStyle}`;
    }
  }
  return chartType;
}

function getDateRangeText(timeFilter: TimeFilter, bookmarkParams: InsightsBookmarkParams) {
  const timeSection = getTimeSection(bookmarkParams);
  let {dateRangeType, window, unit, value, rolling} = timeSection;

  let [fromDate, toDate] = Array.isArray(value) ? value : [null, null];
  const formatDate = (date) => moment(date).format(MomentDateFormat.Full);
  if (!isEmpty(timeFilter)) {
    dateRangeType = timeFilter.dateRange.type;
    fromDate = timeFilter.dateRange.from;
    toDate = timeFilter.dateRange.to;
    window = timeFilter.dateRange.window;
    unit = timeFilter.dateRange.window?.unit;
    rolling = timeFilter.dateRange.rolling;
  }
  switch (dateRangeType) {
    case DateRangeType.Between:
      if (fromDate === START_OF_YESTERDAY && toDate === END_OF_YESTERDAY) {
        return `yesterday`;
      } else if (fromDate === START_OF_TODAY && toDate === END_OF_TODAY) {
        return `today`;
      } else {
        if (rolling) {
          const fromDateStr = DateRange.convertRelativeDateToCalendarDate(fromDate);
          const toDateStr = DateRange.convertRelativeDateToCalendarDate(toDate);
          return `${formatDate(fromDateStr)} - ${formatDate(toDateStr)} (rolling range)`;
        } else {
          return `${formatDate(fromDate)} - ${formatDate(toDate)}`;
        }
      }
    case DateRangeType.On:
      if (rolling) {
        const fromDateStr = DateRange.convertRelativeDateToCalendarDate(fromDate);
        return `on ${formatDate(fromDateStr)} (rolling date)`;
      } else {
        return `on ${formatDate(fromDate)}`;
      }
    case DateRangeType.Since:
      if (value === `$today` || value === START_OF_TODAY) {
        return `today`;
      } else if (fromDate === START_OF_YESTERDAY) {
        return `since yesterday`;
      } else if (X_TO_DATE.includes(fromDate)) {
        return getXToDateText(fromDate);
      } else {
        return `since ${formatDate(fromDate)}`;
      }
    case DateRangeType.Relative:
      return `last ${window.value} ${pluralize(window.unit, window.value)}`;
    default:
      if (value === `$today`) {
        return `today`;
      } else if (isNumber(value)) {
        return `last ${value} ${pluralize(unit, value)}`;
      } else if (fromDate === toDate) {
        return `on ${formatDate(fromDate)}`;
      } else {
        return `${formatDate(fromDate)} - ${formatDate(toDate)}`;
      }
  }
}

function getDisplayOptions(bookmarkParams, chartOptions): DisplayOptions {
  const bookmarkDisplayOptions = {...bookmarkParams.displayOptions};
  const displayOptions =
    bookmarkDisplayOptions.chartType === DeprecatedChartType.SegmentedBar
      ? {...bookmarkDisplayOptions, ...INSIGHTS_CHART_TO_DISPLAY[ChartType.Bar]}
      : bookmarkDisplayOptions;
  if (chartOptions && chartOptions.type) {
    // dirty .type null check to accommodate unanticipated malformed chartOptions
    return {...displayOptions, ...INSIGHTS_CHART_TO_DISPLAY[chartOptions.type]};
  }
  return displayOptions;
}

function getTimeSection(bookmarkParams) {
  return bookmarkParams.sections.time[0];
}

function getFunnelChartDescription({
  bookmark,
  chartOptions,
  timeFilter,
  i18n,
}: {
  bookmark: Bookmark<InsightsBookmarkParams>;
  chartOptions: DashboardCardChartOptions;
  timeFilter: TimeFilter;
  i18n: LocalizationStore;
}) {
  const {type: chartType} = chartOptions;
  // this is guaranteed as pre-condition for this function
  const funnelShowClause = bookmark.params.sections.show.filter((show) => !show.isHidden).find(isBehaviorShowClause);
  let timeComparisonText = ``;
  if (bookmark.params.timeComparison) {
    const comparisonText = i18n.print(getTimeComparisonText(bookmark.params.timeComparison));
    if (comparisonText) {
      timeComparisonText = ` ${comparisonText}`;
    }
  }

  const dateRangeText = `${getDateRangeText(timeFilter, bookmark.params)}${timeComparisonText}`;
  let selectedStep = funnelShowClause?.measurement.stepIndex;
  let subLabel = ``;
  // Frequency should always show step being viewed
  if (chartType === ChartType.FunnelFrequencyLine && !selectedStep) {
    selectedStep = 0;
  }

  if (isNumber(selectedStep)) {
    if (selectedStep === 0) {
      subLabel = `• ${i18n.t(`boards.cards.report-header.entering-funnel`)} `;
    } else {
      const firstStep = selectedStep;
      const secondStep = selectedStep + 1;
      subLabel = `• ${i18n.t(`boards.cards.report-header.funnel-step-from-to`, {from: firstStep, to: secondStep})} `;
    }
  }
  const numSteps = funnelShowClause.behavior.behaviors.length;

  return `${i18n.t(`boards.cards.report-header.multi-step-funnel`, {count: numSteps})} ${subLabel} • ${dateRangeText}`;
}

const OperatorToCardLabel: Record<any, Translatable> = {
  [ALL_MATH_TYPES.UNIQUE]: ({t}) => t(`boards.cards.report-header.unique-users`),
  [ALL_MATH_TYPES.TOTAL]: ({t}) => t(`boards.cards.report-header.total-count`),
  [FUNNELS_MATH_TYPES.GENERAL]: ({t}) => t(`boards.cards.report-header.total-count`),
  [RETENTION_MATH_TYPES.RETENTION_RATE]: ({t}) => t(`query-builder.count-menu.retention-rate`),
};

function getOperatorLabel(operator: MATH_TYPE): Translatable {
  if (operator in OperatorToCardLabel) {
    return OperatorToCardLabel[operator];
  } else if (operator in OperatorToString) {
    return OperatorToString[operator];
  }
  return operator;
}

export function getChartDescription({
  bookmark,
  results,
  chartOptions,
  i18n,
  timeFilter = {},
  dataGroups = [],
}: {
  bookmark: Bookmark<InsightsBookmarkParams>;
  results: InsightsQueryResults;
  chartOptions: DashboardCardChartOptions;
  i18n: LocalizationStore;
  timeFilter?: TimeFilter;
  dataGroups?: DataGroup[];
}) {
  if (isFunnelOnlyInsightsBookmark(bookmark.params)) {
    return getFunnelChartDescription({bookmark, chartOptions, timeFilter, i18n});
  }
  const behaviorClauses = getBehaviorShowClauses(bookmark.params.sections.show);
  const firstBehaviorShowClause = behaviorClauses[0];

  let timeComparisonText = ``;
  if (bookmark.params.timeComparison) {
    const comparisonText = i18n.print(getTimeComparisonText(bookmark.params.timeComparison));
    if (comparisonText) {
      timeComparisonText = ` ${comparisonText}`;
    }
  }
  const dateRangeText = `${getDateRangeText(timeFilter, bookmark.params)}${timeComparisonText}`;

  if (behaviorClauses.length > 0 && behaviorClauses[0].behavior.resourceType === InsightsResourceType.People) {
    const headers = getSeriesDisplayHeaders(
      bookmark.params.sections,
      results.headers,
      keyBy(dataGroups, `groupID`),
      i18n,
    );
    return headers.join(` by `);
  }

  let queryType = ``;
  if (behaviorClauses.length) {
    const operator = firstBehaviorShowClause.measurement.math;
    queryType = i18n.print(getOperatorLabel(operator));
    queryType = queryType + `, `;
  }

  const chartType = getChartType(bookmark.params, chartOptions);

  if (chartType === ChartType.Line || chartType === ChartType.Column) {
    const displayOptions = getDisplayOptions(bookmark.params, chartOptions);
    const analysis = displayOptions.analysis;
    const analysisLabel = analysis ? i18n.t(AnalysisTypeLabelKey[analysis]) : ``;
    if (analysis === AnalysisType.Rolling) {
      const separator = analysisLabel ? ` ` : ``;
      const timeUnit = getTimeSection(bookmark.params).unit;
      const windowSize = displayOptions.rollingWindowSize;
      return `${analysisLabel}${separator}(${windowSize} ${timeUnit}s), ${queryType}${dateRangeText}`;
    } else {
      const separator = analysisLabel ? `, ` : ``;
      return `${analysisLabel}${separator}${queryType}${dateRangeText}`;
    }
  }

  if (chartType === ChartType.Table) {
    // table chart always shows the metric column, which includes the query type (Total, Unique, etc.). \
    // This can also differ between multiple metrics. Omit it from description
    return dateRangeText;
  }

  return `${queryType}${dateRangeText}`;
}

export function isEmptyInsightsResult(results: InsightsQueryResults) {
  return isEmpty(results.series);
}
